import React, { useEffect, useRef } from 'react';
import { CirclePause, X } from 'lucide-react';
import '../styles/CampaignMaintenanceDialog.css';

const DEFAULT_MESSAGE =
  'Campaign creation is temporarily unavailable while SiteSift completes maintenance.';

export default function CampaignMaintenanceDialog({ isOpen, message, onClose }) {
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const maintenanceMessage = typeof message === 'string' && message.trim()
    ? message.trim()
    : DEFAULT_MESSAGE;

  return (
    <div
      className="campaign-maintenance-backdrop"
      data-testid="campaign-maintenance-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="campaign-maintenance-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-maintenance-title"
        aria-describedby="campaign-maintenance-message"
      >
        <div className="campaign-maintenance-icon" aria-hidden="true">
          <CirclePause size={22} />
        </div>
        <h2 id="campaign-maintenance-title">
          Campaign creation is temporarily unavailable
        </h2>
        <p id="campaign-maintenance-message">{maintenanceMessage}</p>
        <button
          ref={closeButtonRef}
          type="button"
          className="campaign-maintenance-close"
          onClick={onClose}
        >
          <X size={17} aria-hidden="true" />
          Close
        </button>
      </section>
    </div>
  );
}
